import { AwsRum, AwsRumConfig, PageAttributes } from 'aws-rum-web';

// Helper function to extract page attributes from the URL path
function getPageAttributes(path: string, inspectionId?: string): PageAttributes {
  const attributes: Record<string, string> = {};
  let pageId = path;

  // Extract inspection ID from path
  const inspectionMatch = path.match(/\/inspections\/detail\/([^/]+)/);
  if (inspectionMatch) {
    pageId = '/inspections/detail/id';
    attributes.inspectionId = inspectionMatch[1];
  }

  // Handle inspection login page - use inspectionId from state if available
  if (path === '/inspection/login') {
    pageId = '/inspection/login';
    // use the inspectionId from the state
    attributes.inspectionId = inspectionId || '';
  }

  // Extract verification ID from path
  const verificationMatch = path.match(/\/verifications\/detail\/([^/]+)/);
  if (verificationMatch) {
    pageId = '/verifications/detail/id';
    attributes.verificationId = verificationMatch[1];
  }

  // Extract seller detail ID from path
  const sellerDetailMatch = path.match(/\/seller\/detail\/([^/]+)/);
  if (sellerDetailMatch) {
    pageId = '/seller/detail/id';
    attributes.sellerId = sellerDetailMatch[1];
  }

  // Extract inspection invoice ID from path
  const invoiceMatch = path.match(/\/inspections\/generateInvoice\/([^/]+)/);
  if (invoiceMatch) {
    pageId = '/inspections/generateInvoice/id';
    attributes.invoiceId = invoiceMatch[1];
  }

  const result: PageAttributes = {
    pageId: pageId,
    pageAttributes: attributes
  };

  return result;
}

function initializeRUM(): AwsRum | null {
  const hostname = window.location.hostname;

  // the per-environment RUM application ID, or null if disabled
  let rumApplicationId: string | null = null;

  if (
    hostname.endsWith('authentik8.sandbox.aws.valeur.io') ||
    hostname === 'localhost'
  ) {
    rumApplicationId = '66456259-c7f3-431f-a79c-851339cc20b4';
  } else if (hostname.indexOf('authentik8.ak8demo.aws.valeur.io') !== -1) {
    rumApplicationId = '27b13298-707e-4548-a7e5-eedcd466572f';
  } else if (hostname.indexOf('app.authentik8.com.au') !== -1) {
    rumApplicationId = '682e3070-62a1-4008-b3d9-638f82728725';
  }

  // Hostnames without an application ID are not monitored.
  if (!rumApplicationId) return null;

  function isInterestingEvent(event: PerformanceResourceTiming): boolean {
    // filters out development resources
    if (hostname === 'localhost' && (event.name.includes("/src/") || event.name.includes("/node_modules/"))) {
      return false;
    }

    // include slow or failed requests of all types
    if (event.responseStatus && event.responseStatus >= 400) {
      return true;
    }
    if (event.duration && event.duration >= 500) {
      return true;
    }

    // Authentik8 API calls, excluding common readonly events
    if (!event.name.includes("/api/")) {
      return false;
    }
    if (event.name.includes("/api/get") || event.name.includes("/api/list")) {
      return false;
    }
    return true;
  }

  try {
    // custom performance tracing, especially for slow or failed API calls
    const performanceTelemetry = ['performance', {
      sampleTypes: ['document', 'font', 'script', 'stylesheet', 'other'],
      eventLimit: 20,

      ignore: (entry: PerformanceEntry) => {
        if (entry instanceof PerformanceResourceTiming) {
          return !isInterestingEvent(entry)
        }
        return true;
      }
    }];

    // HTTP telemetry configuration to record all API calls
    // Excludes polling APIs and list endpoints to reduce noise
    const httpTelemetry = ['http', {
      recordAllRequests: true,
      urlsToInclude: [/\/api\/.*/],
      urlsToExclude: [/\/api\/list.*/, /\/api\/getInspectionFileOcr/]
    }];

    const config: AwsRumConfig = {
      sessionSampleRate: 1,
      endpoint: 'https://dataplane.rum.ap-southeast-2.amazonaws.com',
      telemetries: [performanceTelemetry, 'errors', httpTelemetry],
      allowCookies: true,
      enableXRay: false,
      alias: 'authentik8',
      disableAutoPageView: true,
      signing: false
    };

    const awsRum = new AwsRum(
      rumApplicationId,
      '1.0.0',
      'ap-southeast-2',
      config
    );

    return awsRum;
  } catch (error) {
    console.error('CloudWatch RUM initialization failed:', error);
    return null;
  }
}

// Initialize RUM monitor once and store it
let rumInstance: AwsRum | null = null;

// Initialize the monitor on first use
function getRumMonitor() {
  if (rumInstance === null) {
    rumInstance = initializeRUM();
  }
  return rumInstance;
}

// Export a type-safe page view recorder
export function recordPageView(path: string, inspectionId?: string) {
  const rumMonitor = getRumMonitor();
  if (!rumMonitor) return;

  const data = getPageAttributes(path, inspectionId);
  rumMonitor.recordPageView(data);
}

// RUM's JS error event only keeps name, message and stack from the recorded
// object, so the error itself must be passed rather than a wrapper.
export function recordError(errorContext?: Record<string, any>) {
  const rumMonitor = getRumMonitor();
  if (!rumMonitor) return;

  const error = errorContext?.error ?? errorContext?.message ?? 'Unknown error';
  rumMonitor.recordError(error);
  // The JS error event cannot carry a label, so the caller-supplied type
  // (for example LOCATION_PERMISSION_ERROR) goes on its own event.
  rumMonitor.recordEvent('AppError', {
    errorType: errorContext?.errorType,
    message: error?.message ?? errorContext?.message,
    location: errorContext?.location ?? window.location.pathname,
    userAgent: errorContext?.userAgent,
    timestamp: errorContext?.timestamp ?? new Date().toISOString()
  });
}

/**
 * Record a failed API call as a JS error plus an `ApiError` custom event.
 * Only selected fields are sent: the axios error carries request headers
 * (auth token) and request bodies (base64 images) that must not leave the app.
 */
export function recordAPIError(e: any, responseData?: any) {
  const rumMonitor = getRumMonitor();
  if (!rumMonitor) return;

  rumMonitor.recordError(e ?? 'Unknown API error');
  rumMonitor.recordEvent('ApiError', {
    errorType: 'API_ERROR',
    message: e?.message,
    errorCode: e?.code,
    status: e?.response?.status,
    serverErrorCode: responseData?.error?.code,
    serverErrorMessage: responseData?.error?.message,
    url: e?.config?.url,
    method: e?.config?.method,
    location: window.location.pathname,
    timestamp: new Date().toISOString()
  });
}

/**
 * Type for file upload event metrics
 */
export type FileUploadEvent = {
  inspectionId: string;
  documentType: string;
  originalFileSize: number;
  finalFileSize: number;
  locationWaitTimeMs: number;
  compressionTimeMs: number;
  apiTimeMs: number;
  success: boolean;
  error?: string;
};

/**
 * Record a custom RUM event for file upload tracking
 * Captures timing metrics for location, compression, and API call
 */
export function recordFileUploadEvent(event: FileUploadEvent) {
  const rumMonitor = getRumMonitor();
  if (!rumMonitor) return;

  rumMonitor.recordEvent('FileUpload', {
    inspectionId: event.inspectionId,
    documentType: event.documentType,
    originalFileSize: event.originalFileSize,
    finalFileSize: event.finalFileSize,
    locationWaitTimeMs: event.locationWaitTimeMs,
    compressionTimeMs: event.compressionTimeMs,
    apiTimeMs: event.apiTimeMs,
    success: event.success,
    error: event.error,
    location: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Type for API call event
 */
export type APICallEvent = {
  url: string;
  method: string;
  statusCode: number;
  duration: number;
};

export const localStorageSetItemEventName = 'localStorageSet';

/**
 * Dispatch a custom event when localStorage is set for monitoring purposes
 */
export function dispatchLocalStorageSetItemEvent() {
  // Store the original setItem method
  const originalSetItem = localStorage.setItem;

  // Override setItem to dispatch a custom event
  localStorage.setItem = function (key, value) {
    const event = new CustomEvent(localStorageSetItemEventName, {
      detail: {
        key,
        value
      }
    });
    originalSetItem.apply(this, [key, value]); // Call the original setItem
    document.dispatchEvent(event); // Dispatch the custom event
  };

}